import { Component, EventEmitter, Input, OnInit, OnDestroy, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { DataProvider } from 'projects/packcert-web/src/app/providers/data.provider';
import { Message, SpinnerService } from 'shared-components';
import { UsuariosService } from '../../../services/usuarios.service';
import * as _timezones from 'projects/shared-components/src/lib/assets/timezone.json';
import { Subject } from 'rxjs';
import { SessionContextService } from 'projects/packcert-web/src/app/context/session-context.service';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-eqs-new-edit',
    templateUrl: './new-edit.component.html'
})
export class EqsNewEditComponent implements OnInit, OnDestroy {

    private readonly destroy$ = new Subject<void>();
    private _etiquetador;
    private _provincia;
    public newEditForm: UntypedFormGroup;
    public showError: boolean;
    public showApiError: boolean; // evitar mostrar la clase de error en inputs si falla el getById
    public mensajes = new Array<Message>();
    public toCloseSpinner = 0;

    private _filtersDropDown;
    public paises;
    public paisesEtiquetador;
    public provinciasEtiquetador;
    public municipiosEtiquetador;
    public disabledPais: Subject<boolean> = new Subject();
    public disabledPaisEtiquetador: Subject<boolean> = new Subject();
    public disabledProvinciaEtiquetador: Subject<boolean> = new Subject();
    public disabledMunicipioEtiquetador: Subject<boolean> = new Subject();
    public disabledIdioma: Subject<boolean> = new Subject();
    public disabledTimezone: Subject<boolean> = new Subject();

    public showPassword = true;
    public showModal = false;
    public usuarioExistente;

    @Input() etiquetadorId;
    @Input() applicationUserId;

    @Output() refresh = new EventEmitter<boolean>();
    @Output() mensaje = new EventEmitter<string>();

    constructor(
        private _fb: UntypedFormBuilder,
        private readonly _spinnerService: SpinnerService,
        private readonly _dataProvider: DataProvider,
        private readonly _usuariosService: UsuariosService,
        private readonly _sessionContext: SessionContextService
    ) {}

    ngOnInit() {
        this.newEditForm = this._fb.group({
            etiquetador: ['', Validators.required],
            userName: ['', [Validators.required, Validators.pattern('^[A-z0-9._%+-]+@[A-z0-9.-]+\\.[a-z]{2,4}$')]],
            password: ['', Validators.required],
            phoneNumber: [''],
            useTwoFactor: [true],
            idioma: ['', Validators.required],
            msTimeZone: [''],
            direccion: [''],
            nombre: ['', Validators.required],
            apellido: ['', Validators.required],
            activo: [true]
        });

        this.newEditForm.get('userName').valueChanges
        .pipe(
            debounceTime(1000),
            distinctUntilChanged(),
            takeUntil(this.destroy$)
        )
        .subscribe((userName) => {
            if (!this.newEditForm.get('userName').hasError('pattern') && !this.etiquetadorId && !this.showModal &&
                this.role === 'Administrador' && userName !== '' && userName !== null) {
                document.getElementById('userName').blur();
                this._spinnerService.showSpinner();
                const usuario = {
                    userName,
                    roleName: 'Etiquetador'
                };
                this._usuariosService.checkUsuarioExisteYAsociadoARol(usuario).toPromise().finally(() => {
                    this._spinnerService.hideSpinner();
                }).then((resp) => {
                    if (resp) {
                        if (resp.asociadoARol) {
                            this.showModal = true;
                        } else {
                            this.usuarioExistente = resp;
                            this.showPassword = false;
                            this.newEditForm.get('password').clearValidators();
                            this.newEditForm.get('password').updateValueAndValidity({onlySelf: true, emitEvent: false});
                            this.fillInfo();
                        }
                    } else {
                        this.newEditForm.get('nombre').enable();
                        this.newEditForm.get('apellido').enable();
                        this.newEditForm.get('phoneNumber').enable();
                        this.newEditForm.get('direccion').enable();
                        this.newEditForm.get('idioma').enable();
                        this.newEditForm.get('msTimeZone').enable();

                        this.disabledIdioma.next(false);
                        this.disabledTimezone.next(false);

                        document.getElementById('userName').focus();

                        this.showPassword = true;
                        this.newEditForm.get('password').setValidators([Validators.required]);
                        this.newEditForm.get('password').updateValueAndValidity({onlySelf: true, emitEvent: false});
                    }
                });
            } else {
                if (!this.etiquetadorId) {
                    this.newEditForm.get('nombre').enable();
                    this.newEditForm.get('apellido').enable();
                    this.newEditForm.get('phoneNumber').enable();
                    this.newEditForm.get('direccion').enable();
                    this.newEditForm.get('idioma').enable();
                    this.newEditForm.get('msTimeZone').enable();
                    this.newEditForm.get('useTwoFactor').enable();
                    this.newEditForm.get('activo').enable();
                    this.newEditForm.get('nombre').reset();
                    this.newEditForm.get('apellido').reset();
                    this.newEditForm.get('phoneNumber').reset();
                    this.newEditForm.get('direccion').reset();
                    this.newEditForm.get('idioma').reset();
                    this.newEditForm.get('msTimeZone').reset();
                    this.newEditForm.get('useTwoFactor').reset();
    
                    this.disabledIdioma.next(false);
                    this.disabledTimezone.next(false);
    
                    document.getElementById('userName').focus();
    
                    this.showPassword = true;
                    this.newEditForm.get('password').setValidators([Validators.required]);
                    this.newEditForm.get('password').updateValueAndValidity({onlySelf: true, emitEvent: false});
                }

            }
        });

        if (this.etiquetadorId) {
            this.getEtiquetador();
        }

    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public get role() {
        return this._sessionContext.sessionUser.role;
    }

    public get etiquetadores() {
        return this._dataProvider.etiquetadores;
    }

    public get idiomas() {
        return this._dataProvider.idiomas;
    }

    public get timezones() {
        return this._dataProvider.timeZones;
    }

    public get isSmallScreen() {
        return this._sessionContext.isSmallScreen();
    }

    public getEtiquetador(): Promise<any> {
        return new Promise((resolve, reject) => {
            this._spinnerService.showSpinner();
            this.toCloseSpinner += 1;
            this._usuariosService.getEtiquetador(this.etiquetadorId, this.applicationUserId).toPromise().finally(() => {
                this.checkCloseSpinner();
            }).then((resp) => {
                this._etiquetador = resp;
                this.newEditForm.patchValue({
                    etiquetador: this.etiquetadores.find(x => x.id === resp.etiquetadorId),
                    nombre: resp.nombre,
                    apellido: resp.apellido,
                    idioma: this.idiomas.find(x => x.id === resp.idiomaId),
                    msTimeZone: this.timezones.find(x => x.id === resp.msTimeZoneId),
                    userName: resp.userName,
                    useTwoFactor: resp.twoFactorEnabled,
                    activo: resp.activo
                });

                if (resp.userName) {
                    this.newEditForm.get('userName').disable();
                    this.newEditForm.get('userName').clearValidators();
                    this.newEditForm.get('userName').updateValueAndValidity({onlySelf: true, emitEvent: false});
                    this.newEditForm.get('password').disable();
                    this.newEditForm.get('password').clearValidators();
                    this.newEditForm.get('password').updateValueAndValidity({onlySelf: true, emitEvent: false});
                }
                setTimeout(resolve, 1);
            }, (error) => {
                this.showApiError = true;
                if (error.status === 420) {
                    this.mensajes.push({ class: 'error', message: 'global.error.noAccess' });
                } else {
                    this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
                }
            });
        });
    }

    // public getProvinciasPais() : Promise<any> {
    //     return new Promise((resolve, reject) => {
    //         this._filtersDropDown = {
    //             paisId: this._etiquetador.paisId
    //         }
    //         this._spinnerService.showSpinner();
    //         this.toCloseSpinner += 1;
    //         this._usuariosService.getProvinciasPais(this._filtersDropDown).toPromise().finally(() => {
    //             this.checkCloseSpinner();
    //         }).then((resp) => {
    //             if (resp.lista.length > 0) {
    //             this.provinciasEtiquetador = resp.lista;
    //             this.newEditForm.get('municipioProductor').setValidators(Validators.required);
    //             this.newEditForm.get('municipioProductor').enable();
    //             this.newEditForm.get('municipioProductor').updateValueAndValidity();
    //             this.disabledProvinciaEtiquetador.next(false);
    //             }
    //             setTimeout(resolve,1);
    //         });
    //     });
    // }

    // public getMunicipiosProvincia() {
    //     return new Promise((resolve, reject) => {
    //       this._filtersDropDown = {
    //         provinciaId: this._etiquetador.provinciaId
    //       }
    //       this._spinnerService.showSpinner();
    //       this.toCloseSpinner += 1;
    //       this._usuariosService.getMunicipiosPais(this._filtersDropDown).toPromise().finally(() => {
    //         this.checkCloseSpinner();
    //       }).then((resp) => {
    //         if (resp.lista.length > 0) {
    //           this.municipiosEtiquetador = resp.lista;
    //         }
    //         setTimeout(resolve,1);
    //       });
    //     });
    // }

    public close() {
        this.refresh.emit(false);
    }

    public save() {
        this.checkForm();
        if (this.newEditForm.valid && !this.etiquetadorId) {
            this._etiquetador = {
                etiquetadorId: this.newEditForm.get('etiquetador').value ? this.newEditForm.get('etiquetador').value.id : null,
                userName: this.newEditForm.get('userName').value ? this.newEditForm.get('userName').value : null,
                password: this.newEditForm.get('password').value,
                phoneNumber: this.newEditForm.get('phoneNumber').value ? this.newEditForm.get('phoneNumber').value : '',
                useTwoFactor: this.newEditForm.get('useTwoFactor').value ? this.newEditForm.get('useTwoFactor').value : false,
                idiomaId: this.newEditForm.get('idioma').value ? this.newEditForm.get('idioma').value.id : '',
                nombre: this.newEditForm.get('nombre').value,
                apellido: this.newEditForm.get('apellido').value,
                msTimeZoneId: this.newEditForm.get('msTimeZone').value ? this.newEditForm.get('msTimeZone').value.id : null,
                direccion: this.newEditForm.get('direccion').value ? this.newEditForm.get('direccion').value : '',
                activo: true
            };
            this._spinnerService.showSpinner();
            this.toCloseSpinner += 1;
            this._usuariosService.asociarUsuarioAEtiquetador(this._etiquetador).toPromise().finally(() => {
                this.checkCloseSpinner();
            }).then((resp) => {
                if (resp.id) {
                    this.refresh.emit(true);
                    this.mensaje.emit('global.success.itemCreated');
                } else {
                    this.showError = true;
                    this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
                }
            },
            (error) => {
                if (error.status === 420) {
                  switch (error.error.ApiErrorCode) {
                      case 1027:
                          this.pushMessage(error.error.Descripcion);
                          break;
                  }
                }
            });
        } else if (this.newEditForm.valid && this.etiquetadorId) {
            this._etiquetador = {
                applicationUserId: this.applicationUserId,
                etiquetadorId: this.newEditForm.get('etiquetador').value ? this.newEditForm.get('etiquetador').value.id : null,
                useTwoFactor: this.newEditForm.get('useTwoFactor').value ? this.newEditForm.get('useTwoFactor').value : false,
                idiomaId: this.newEditForm.get('idioma').value ? this.newEditForm.get('idioma').value.id : '',
                nombre: this.newEditForm.get('nombre').value,
                apellido: this.newEditForm.get('apellido').value,
                msTimeZoneId: this.newEditForm.get('msTimeZone').value ? this.newEditForm.get('msTimeZone').value.id : null,
                activo: this.newEditForm.get('activo').value ? this.newEditForm.get('activo').value : false
              };
              this._spinnerService.showSpinner();
            this.toCloseSpinner += 1;
            this._usuariosService.updateUsuarioAEmpacador(this._etiquetador).toPromise().finally(() => {
                this.checkCloseSpinner();
            }).then(
                (resp) => {
                  this.refresh.emit(true);
                  this.mensaje.emit('global.success.itemUpdated');
                },
                (error) => {
                  this.showError = true;
                  this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
                }
            );
        }
    }

    private checkForm() {
        this.showError = this.newEditForm.invalid;
        if (this.showError) {
          this.mensajes.push({ class: 'error', message: 'global.error.required' });
        }
    }

    private checkCloseSpinner() {
        this.toCloseSpinner -= 1;
        if (this.toCloseSpinner === 0) {
          this._spinnerService.hideSpinner();
        }
    }

    public pushMessage(mensaje) {
        this.showError = true;
        this.mensajes.push({ class: 'error', message: mensaje });
    }

    public fillInfo() {
        this.newEditForm.patchValue({
            etiquetador: this.etiquetadores.find(x => x.id === this.usuarioExistente.etiquetadorId),
            nombre: this.usuarioExistente.nombre,
            apellido: this.usuarioExistente.apellido,
            phoneNumber: this.usuarioExistente.phoneNumber,
            direccion: this.usuarioExistente.direccion,
            idioma: this.idiomas.find(x => x.id === this.usuarioExistente.idiomaId),
            msTimeZone: this.timezones.find(x => x.id === this.usuarioExistente.msTimeZoneId),
            useTwoFactor: this.usuarioExistente.twoFactorEnabled,
            activo: this.usuarioExistente.activo
        });

        this.newEditForm.get('nombre').disable();
        this.newEditForm.get('apellido').disable();
        this.newEditForm.get('idioma').disable();
        this.newEditForm.get('msTimeZone').disable();
        this.newEditForm.get('direccion').disable();
        this.newEditForm.get('phoneNumber').disable();
        this.newEditForm.get('useTwoFactor').disable();
        this.newEditForm.get('activo').disable();


        this.disabledIdioma.next(true);
        this.disabledTimezone.next(true);

        this.showModal = false;

        this.showPassword = false;
        this.newEditForm.get('password').clearValidators();
        this.newEditForm.get('password').updateValueAndValidity({onlySelf: true, emitEvent: false});
    }

    public hideModal() {
        this.showModal = false;

        this.showPassword = true;
        this.newEditForm.get('password').setValidators([Validators.required]);
        this.newEditForm.get('password').updateValueAndValidity({onlySelf: true, emitEvent: false});

        this.newEditForm.get('nombre').enable();
        this.newEditForm.get('apellido').enable();
        this.newEditForm.get('phoneNumber').enable();
        this.newEditForm.get('direccion').enable();
        this.newEditForm.get('idioma').enable();
        this.newEditForm.get('msTimeZone').enable();

        this.disabledIdioma.next(false);
        this.disabledTimezone.next(false);
    }
}
